import { css } from '@emotion/react'
import { typo } from '@mathflat/common/style'
import clsx from 'clsx'
import type { ReactNode } from 'react'

import { Icon } from '@/component/atoms/Icon'
import Name from '@/component/atoms/Name'

import type { DualListItem as DualListItemType, RenderItemExtraParams } from '../types'

type Props<T extends DualListItemType> = {
  item: T
  side: 'left' | 'right'
  disabled?: boolean
  renderExtra?: (params: RenderItemExtraParams<T>) => ReactNode
  grouped?: boolean
  onClick: () => void
}

const DualListItem = <T extends DualListItemType>({
  item,
  side,
  disabled = false,
  grouped = false,
  renderExtra,
  onClick,
}: Props<T>) => {
  const iconName = side === 'left' ? 'add_circle' : 'remove_fill'
  const iconColor = side === 'left' ? 'var(--Colors-Sky-400)' : 'var(--Colors-Gray-900)'
  const extraContent = renderExtra?.({ item, side, disabled })
  return (
    <button
      type="button"
      css={S.itemButton}
      className={clsx({ grouped })}
      disabled={disabled}
      onClick={onClick}
      data-mathflat-unique-id={item.id}
    >
      <Name maxWidth={extraContent ? 89 : 116} elipsisLines={1}>
        {item.name}
      </Name>

      <div css={S.rightSection}>
        {extraContent && extraContent}

        <div className="icon-wrapper">
          <Icon name={iconName} size={20} color={disabled ? 'var(--Colors-Gray-500)' : iconColor} />
        </div>
      </div>
    </button>
  )
}

export default DualListItem

const S = {
  itemButton: css`
    display: flex;
    align-items: center;
    width: 100%;
    height: 48px;
    padding: 0 24px;
    border-bottom: 1px solid var(--Colors-Gray-300);
    color: var(--Colors-Gray-800);
    font-weight: 500;
    cursor: pointer;

    &.grouped {
      padding-left: 52px;
    }
    &:hover {
      background-color: var(--Colors-Gray-100);
    }
    &:disabled {
      cursor: not-allowed;
      color: var(--Colors-Gray-500);
    }
    .icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      width: 20px;
      margin-left: 12px;
    }
    .item-extra {
      margin-left: 8px;
      color: var(--Colors-Gray-700);
      ${typo.caption02};
    }
  `,
  rightSection: css`
    display: flex;
    margin-left: auto;
  `,
}
